import { Button, Form, Input, Typography, Card, notification } from 'antd';
import { IoChevronBack } from 'react-icons/io5';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useMutation } from '@tanstack/react-query';
import { ManualInsertLeadProps } from '@/pages/Novi/types/Leads';
import { addNewLead } from '@/api/novi/leads';
import { useEffect, useMemo } from 'react';
import { getSelectedAgent } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

const { Title } = Typography;

const ManualInsertLead: React.FC<ManualInsertLeadProps> = ({ onClose, goBack }) => {
	const selectedCampaign = useAppSelector(getSelectedAgent);

	const variables = useMemo(() => {
		return safeJSONParse(selectedCampaign?.variables, []);
	}, [selectedCampaign?.variables]); // ← MEMOIZE

	const [form] = Form.useForm();

	useEffect(() => {
		if (variables) {
			const defaultValues = Object.entries(variables).reduce(
				(acc, [key, value]: [string, any]) => ({
					...acc,
					[key]: value.defaultValue || '',
				}),
				{},
			);
			form.setFieldsValue(defaultValues);
		}
	}, [variables, form]); // ← only when campaign variables change

	const validatePhoneNumber = (_: any, value: string) => {
		if (!value) {
			return Promise.reject('Phone number is required');
		}
		try {
			if (!isValidPhoneNumber(value, 'US')) {
				return Promise.reject('Please enter a valid US phone number');
			}
			const phoneNumber = parsePhoneNumber(value, 'US');
			form.setFieldValue('phone_number', phoneNumber.format('E.164'));
			return Promise.resolve();
		} catch (error) {
			return Promise.reject('Please enter a valid phone number');
		}
	};

	const mutation = useMutation({
		mutationKey: ['createLead'],
		mutationFn: addNewLead,
		onSuccess: () => {
			notification.success({
				message: 'Success',
				description: 'Lead created successfully',
			});
			form.resetFields();
			onClose();
		},
		onError: (error) => {
			notification.error({
				message: 'Error',
				description: error.message,
			});
		},
	});

	const handleFinish = (values: { [key: string]: string }) => {
		const { phone_number, ...variables } = values;
		mutation.mutate({
			campaign_id: selectedCampaign?.number,
			lead: {
				phone_number,
				variables,
			},
		});
	};

	return (
		<div style={{ padding: '20px', backgroundColor: '#fff' }}>
			<Button
				type="link"
				icon={<IoChevronBack />}
				onClick={goBack}
				style={{ marginBottom: '20px' }}
			>
				Choose another method
			</Button>

			<Card
				title={<Title level={2}>Insert a Lead</Title>}
				style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}
			>
				<Form form={form} layout="vertical" onFinish={handleFinish}>
					<Form.Item
						name="phone_number"
						label="Phone Number"
						rules={[
							{ required: true, message: 'Please input the phone number' },
							{ validator: validatePhoneNumber },
						]}
					>
						<Input />
					</Form.Item>

					{variables &&
						Object.entries(variables).map(([variable, config]: [string, any]) => (
							<Form.Item
								key={variable}
								name={variable}
								label={variable.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
								rules={[{ required: config.isMandatory, message: `Please input the ${variable}` }]}
							>
								<Input />
							</Form.Item>
						))}

					<Button
						type="primary"
						htmlType="submit"
						style={{ width: '100%' }}
						loading={mutation.isPending}
					>
						Add a new lead
					</Button>
				</Form>
			</Card>
		</div>
	);
};

export default ManualInsertLead;
